import React from 'react';
import clsx from 'clsx';

import { usePageLayoutContext } from '../services/context/pageLayoutContext';

type Props = {
  children: React.ReactNode;
  dataTest?: string;
  isSuppressed?: boolean;
};

/**
 * Standardized styled section for all pages.
 * Max width of the content is set to 700px (margins/paddings are not counted).
 *
 * @internal - Do not import into modules. Use {@link PageHeader} or {@link PageSection} or {@link PageFooter} instead!
 * @param children - content
 * @param dataTest - optional dataTest
 * @param isSuppressed - optional isSuppressed, when true the section with have a grey background
 * @returns page section with max width and paddings
 */
const PrimitivePageSection = ({ children, dataTest, isSuppressed }: Props) => {
  const { hasSidebar, hasFixedFooter } = usePageLayoutContext();

  if (isSuppressed) {
    return (
      <div
        className={clsx(
          'w-full de:max-w-[calc(var(--mmb-page-section-max-width)+2*32px)] ld:max-w-[calc(var(--mmb-page-section-max-width)+2*52px)]',
          hasSidebar ? 'ms-auto' : 'm-auto',
        )}
        data-test={dataTest}
      >
        <div className="m-0 bg-cloud-light p-400 de:mx-800 de:p-0 ld:mx-1200 de:[&>*]:p-600">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'px-[var(--mmb-page-section-horizontal-padding)] de:px-[var(--mmb-page-section-horizontal-padding-desktop)] ld:px-[var(--mmb-page-section-horizontal-padding-large-desktop)]',
        'first:py-[var(--mmb-page-section-vertical-padding-desktop)]',
        'last:py-[var(--mmb-page-section-vertical-padding)] last:de:py-[var(--mmb-page-section-vertical-padding-desktop)]',
        // If second child is at the same time also the last child but not fixed footer, set the top padding to 0
        !hasFixedFooter && '[&:nth-child(2):last-child]:pt-0',
      )}
      data-test={dataTest}
    >
      <div
        className={clsx(
          hasSidebar ? 'ms-auto' : 'm-auto',
          'w-full de:max-w-[var(--mmb-page-section-max-width)]',
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default PrimitivePageSection;
